import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField, useAssignees, useLookups } from "@/lib/crm";
import { usePipeline, VISIBILITY } from "@/lib/sales";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CompanyPicker } from "@/components/sales/CompanyPicker";

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

const initial = (o, preset) => ({
  title: o?.title || "", company: o ? { id: o.company_id, name: o.company_name } : preset || null, contact_id: o?.contact_id || "",
  value: o?.value ?? "", expected_close_at: o?.expected_close_at || "", source: o?.source || "", stage_id: "",
  owner_id: o?.owner_id || "", probability: o?.probability ?? "", visibility: o?.visibility || "public", description: o?.description || "",
});

export const OpportunityDialog = ({ open, onOpenChange, opportunity, presetCompany, onSaved }) => {
  const { can } = useAuth();
  const lk = useLookups();
  const users = useAssignees();
  const { stages } = usePipeline();
  const [f, setF] = useState(initial(null));
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const isEdit = !!opportunity;
  const set = (p) => setF((x) => ({ ...x, ...p }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setF(initial(opportunity, presetCompany)); }, [open]);
  useEffect(() => {
    if (!f.company?.id || !can("contacts:read")) { setContacts([]); return; }
    api.get(`/crm/companies/${f.company.id}`).then((r) => {
      setContacts(r.data.contacts.map((c) => ({ id: c.id, name: `${c.first_name} ${c.last_name}`.trim() })));
      const primary = r.data.contacts.find((c) => c.is_primary);
      if (!isEdit && primary) setF((x) => (x.contact_id ? x : { ...x, contact_id: primary.id }));
    }).catch(() => setContacts([]));
  }, [f.company?.id, can, isEdit]);

  const save = async () => {
    setBusy(true);
    const body = { title: f.title.trim(), contact_id: f.contact_id || null, value: f.value === "" ? 0 : Number(f.value),
      expected_close_at: f.expected_close_at || null, source: f.source || null, description: f.description || null, visibility: f.visibility };
    if (can("opportunities:assign") && f.owner_id) body.owner_id = f.owner_id;
    try {
      if (isEdit) {
        if (opportunity.status === "open" && f.probability !== "") body.probability = Number(f.probability);
        await api.patch(`/sales/opportunities/${opportunity.id}`, body);
        toast.success("Opportunità aggiornata"); onSaved(opportunity.id);
      } else {
        const r = await api.post("/sales/opportunities", { ...body, company_id: f.company.id, stage_id: f.stage_id || undefined });
        toast.success("Opportunità creata"); onSaved(r.data.id);
      }
      onOpenChange(false);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  const openStages = stages.filter((s) => s.active && s.outcome === "open").map((s) => ({ id: s.id, name: `${s.name} · ${s.probability}%` }));
  const visibility = VISIBILITY.filter((v) => !v.confidential || can("data:confidential"));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="opportunity-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{isEdit ? "Modifica opportunità" : "Nuova opportunità"}</DialogTitle>
          <DialogDescription>Cliente, valore atteso e quando prevedi di chiudere.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Titolo"><Input maxLength={200} value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="Es. Rinnovo infrastruttura di rete" data-testid="opp-input-title" /></Field></div>
          <div className="sm:col-span-2"><CompanyPicker value={f.company} onChange={(c) => set({ company: c, contact_id: "" })} disabled={isEdit || !!presetCompany} allowCreate={can("companies:create")} testId="opp-company" /></div>
          <SelectField label="Referente" value={f.contact_id} onChange={(v) => set({ contact_id: v })} options={contacts} allowNone="Nessuno" testId="opp-input-contact" />
          <SelectField label="Fonte" value={f.source} onChange={(v) => set({ source: v })} options={lk.lead_source} allowNone="—" testId="opp-input-source" />
          <Field label="Valore (€, imponibile)"><Input type="number" min="0" step="0.01" value={f.value} onChange={(e) => set({ value: e.target.value })} data-testid="opp-input-value" /></Field>
          <Field label="Chiusura prevista"><Input type="date" value={f.expected_close_at} onChange={(e) => set({ expected_close_at: e.target.value })} data-testid="opp-input-close" /></Field>
          {!isEdit && <SelectField label="Fase iniziale" value={f.stage_id} onChange={(v) => set({ stage_id: v })} options={openStages} allowNone="Prima fase" testId="opp-input-stage" />}
          {isEdit && opportunity.status === "open" && <Field label="Probabilità (%)"><Input type="number" min="0" max="100" value={f.probability} onChange={(e) => set({ probability: e.target.value })} data-testid="opp-input-probability" /></Field>}
          {can("opportunities:assign") && <SelectField label="Responsabile" value={f.owner_id} onChange={(v) => set({ owner_id: v })} options={users} allowNone="Io" testId="opp-input-owner" />}
          <SelectField label="Visibilità" value={f.visibility} onChange={(v) => set({ visibility: v })} options={visibility} testId="opp-input-visibility" />
          <div className="sm:col-span-2"><Field label="Descrizione"><Textarea rows={3} maxLength={4000} value={f.description} onChange={(e) => set({ description: e.target.value })} data-testid="opp-input-description" /></Field></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={busy || f.title.trim().length < 2 || !f.company} data-testid="opp-save">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
